import React, { useState, useEffect } from 'react'
import { Tag, Table, Button, Tooltip, Modal, Input, Select, message } from 'antd';
import axios from 'axios';
import { useForm, Controller, set } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import dayjs from 'dayjs';
import { EditOutlined } from '@ant-design/icons';

const schema = yup.object().shape({
    version_number: yup.string().required("Version is required"),
    is_force_update: yup.string().required("Force update is required"),
});

const AppUpdate = () => {
    const [loading, setLoading] = useState(false);
    const [details, setDetails] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    
    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            is_force_update: "no",
        },
    });

    const isForceUpdate = [
        {
            value: "no",
            label: "No",
        },
        {
            value: "yes",
            label: "Yes",
        },
    ]

    const handleTableChange = (pagination, filters, sorter) => {
        setCurrentPage(pagination.current);
        setPageSize(pagination.pageSize);
    };

    const handleEdit = (record) => {
        reset(record);
        setEditId(record.id);
        setIsModalOpen(true);
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await axios.get(
                process.env.REACT_APP_BASE_URL + `/api/updates`,
                {
                    params: {
                        limit: pageSize,
                        page: currentPage,
                    },
                }
            )
            const record = response.data;
            setDetails(record);
            setLoading(false);
        } catch (error) {
            setLoading(false);
            console.error("Error fetching app update data", error);
        }
    };

    useEffect(() => {
        const fetchTimer = setTimeout(() => {
            fetchData();
        }, 500);

        return () => clearTimeout(fetchTimer);
    }, [currentPage, pageSize]);

    const onUpdateForm = async (data) => {
        try {
            setLoading(true);
            const response = await axios.put(
                process.env.REACT_APP_BASE_URL + `/api/updates/update`,
                {
                    id: editId,
                    version_number: data.version_number,
                    is_force_update: data.is_force_update
                }
            );
            if (response.status === 200) {
                message.success("App update updated successfully");
            }
            await fetchData();
            setLoading(false);
            setIsModalOpen(false);
        } catch (error) {
            console.error("Error updating app update data", error);
        }
    };

    const columns = [
        {
            title: 'Version Number',
            dataIndex: 'version_number',
            key: 'version_number',
            render: (text) => (
                <span>{text}</span>
            )
        },
        {
            title: 'Platform Type',
            dataIndex: 'platform_name',
            key: 'platform_name',
            render: (text) => (
                <span>{text}</span>
            )
        },
        {
            title: 'Force Update',
            dataIndex: 'is_force_update',
            key: 'update',
            render: (text) => (
                <Tag color={text === "yes" ? 'green' : 'red'}>
                    {text === "yes" ? "Yes" : "No"}
                </Tag>
            )
        },
        {
            title: 'Modified By',
            dataIndex: 'modified_by',
            key: 'modified_by',
            render: (text) => (
                <span>{text}</span>
            )
        },
        {
            title: 'Modified At',
            dataIndex: 'modified_at',
            key: 'modified_at',
            render: (text) => (
                <span className="text-small">{dayjs(text).format('DD-MM-YYYY HH:mm')}</span>
            )
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Tooltip title="Update">
                    <Button
                        icon={<EditOutlined />}
                        size="small"
                        onClick={() => handleEdit(record)}
                    />
                </Tooltip>
            )
        }
    ]
    return (
        <div style={{ padding: '1rem' }} >
            <Table
                loading={loading}
                columns={columns}
                dataSource={details}
                scroll={{ x: "max-content" }}
                rowKey={(record) => record.id}
                onChange={handleTableChange}
                pagination={false}
            />
            {isModalOpen &&
                <Modal
                    title="App Update"
                    open={isModalOpen}
                    onCancel={() => {
                        reset();
                        setIsModalOpen(false)
                    }}
                    footer={null}
                    width={600}
                >
                    <form onSubmit={handleSubmit(onUpdateForm)}>
                        <div className="mb-3">
                            <label className="form-label">
                                Version Number <span className="text-danger">*</span>
                            </label>
                            <Controller
                                name="version_number"
                                control={control}
                                render={({ field }) => <Input {...field} />}
                            />
                            {errors.name && (
                                <div className="text-danger small">{errors.name.message}</div>
                            )}
                        </div>
                        <div className="mb-3">
                            <label className="form-label">
                                Force Update <span className="text-danger">*</span>
                            </label>
                            <Controller
                                name="is_force_update"
                                control={control}
                                render={({ field }) =>
                                    <Select
                                        {...field}
                                        showSearch
                                        placeholder="Select Force Update"
                                        filterOption={(input, option) => {
                                            return (option?.children ?? "")
                                                .toLowerCase()
                                                .includes(input.toLowerCase());
                                        }}
                                        style={{ width: "100%" }}
                                        onChange={field.onChange}
                                    >
                                        {isForceUpdate.map((update) => (
                                            <Select.Option value={update.value} key={update.value}>
                                                {update.label}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                }
                            />
                            {errors.name && (
                                <div className="text-danger small">{errors.name.message}</div>
                            )}
                        </div>
                        <div className="d-flex justify-content-end">
                            <Button type="primary" htmlType="submit" disabled={loading} loading={loading}>
                                Update App
                            </Button>
                        </div>
                    </form>
                </Modal>
            }
        </div>
    )
}

export default AppUpdate;